import { useEffect } from "react";
import { useAuth } from "react-oidc-context";

import { setAccessTokenProvider } from "../cloud/apiClient";

import { buildCognitoLogoutUrl, isAuthConfigured } from "./authConfig";

import type { ReactNode } from "react";

/**
 * Minimal full-screen message used for the loading / error / redirecting
 * states. Kept dependency-free and inline so it renders before the Excalidraw
 * app (and its styles) are mounted.
 */
const Splash = ({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}) => (
  <div
    style={{
      position: "fixed",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "1rem",
      fontFamily:
        "Assistant, system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      color: "var(--color-on-surface, #1b1b1f)",
      padding: "2rem",
      textAlign: "center",
    }}
  >
    <h1 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>{title}</h1>
    {children}
  </div>
);

const buttonStyle: React.CSSProperties = {
  appearance: "none",
  border: "none",
  borderRadius: "0.5rem",
  padding: "0.625rem 1.25rem",
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  background: "#6965db",
  color: "#fff",
};

/**
 * Sign the user out of Cognito (and the upstream Entra session) by removing the
 * local user then redirecting through the Cognito /logout endpoint.
 */
export const signOut = async (
  removeUser: () => Promise<void>,
): Promise<void> => {
  await removeUser();
  window.location.assign(buildCognitoLogoutUrl());
};

/**
 * Gates its children behind a successful Cognito (Entra-federated) sign-in.
 *
 * - While the library resolves the session or handles the redirect callback,
 *   shows a splash.
 * - If unauthenticated, kicks off the Authorization Code + PKCE redirect to
 *   Cognito managed login (which forwards to Entra ID).
 * - Once authenticated, renders the app.
 */
export const AuthGate = ({ children }: { children: ReactNode }) => {
  const auth = useAuth();

  const { isLoading, isAuthenticated, activeNavigator, error, signinRedirect } =
    auth;

  // Feed the live ID token to the cloud API client. We use the ID token (not
  // the access token) because the API Gateway JWT authorizer validates the
  // `aud` claim against the app client id — Cognito sets `aud` on the ID token,
  // whereas access tokens carry the client id in `client_id` (no `aud`). The
  // Lambda derives the user `sub` from the token, which both tokens include.
  useEffect(() => {
    setAccessTokenProvider(() => auth.user?.id_token);
  }, [auth.user]);

  // Trigger the sign-in redirect once we know the user is not authenticated and
  // nothing else is in flight. `isAuthConfigured` is a module-level constant so
  // it is intentionally not in the dependency array.
  useEffect(() => {
    if (
      isAuthConfigured &&
      !isAuthenticated &&
      !isLoading &&
      !activeNavigator &&
      !error
    ) {
      // Preserve the current path (e.g. a /s/<id> share link) across the SSO
      // round-trip so the user lands back where they started after sign-in.
      void signinRedirect({
        state: { returnTo: window.location.pathname + window.location.search },
      });
    }
  }, [isAuthenticated, isLoading, activeNavigator, error, signinRedirect]);

  if (!isAuthConfigured) {
    return (
      <Splash title="Authentication is not configured">
        <p style={{ maxWidth: 480, lineHeight: 1.5 }}>
          The app was built with <code>VITE_APP_AUTH_ENABLED=true</code> but the
          Cognito environment variables are missing. Set{" "}
          <code>VITE_APP_COGNITO_AUTHORITY</code>,{" "}
          <code>VITE_APP_COGNITO_CLIENT_ID</code> and{" "}
          <code>VITE_APP_COGNITO_DOMAIN</code> and rebuild.
        </p>
      </Splash>
    );
  }

  if (error) {
    return (
      <Splash title="Sign-in failed">
        <p style={{ maxWidth: 480, lineHeight: 1.5 }}>{error.message}</p>
        <button
          style={buttonStyle}
          onClick={() =>
            void signinRedirect({
              state: {
                returnTo: window.location.pathname + window.location.search,
              },
            })
          }
          type="button"
        >
          Try again
        </button>
      </Splash>
    );
  }

  if (isLoading || activeNavigator) {
    return <Splash title="Signing you in…" />;
  }

  if (!isAuthenticated) {
    // The effect above is redirecting to Cognito; show a brief splash.
    return <Splash title="Redirecting to sign-in…" />;
  }

  return <>{children}</>;
};
